<template>
  <div>
    <!-- Control bar -->
    <div class="d-flex flex-wrap gap-2 align-items-center bg-semi-transparent rounded p-2 mb-3">
      <select class="form-select form-select-sm w-auto" v-model="districtSel">
        <option value="all">All districts</option>
        <option v-for="d in districts" :key="d.id" :value="d.id">{{ d.name }}</option>
      </select>
      <select class="form-select form-select-sm w-auto" v-model="typeSel">
        <option value="all">All types</option>
        <option value="kjp">NOD</option>
        <option value="emergency">Emergency</option>
        <option value="electrical">Electrical</option>
      </select>
      <button v-if="view === 'table'" class="btn btn-sm btn-outline-success ms-auto" @click="downloadExcel" title="Download as Excel Sheet">
        <i class="fa-solid fa-file-excel me-1" /> Download as Excel Sheet
      </button>
      <div class="btn-group btn-group-sm" :class="{ 'ms-auto': view !== 'table' }">
        <button class="btn" :class="view === 'chart' ? 'btn-primary' : 'btn-outline-primary'" @click="view = 'chart'">
          <i class="fa-solid fa-chart-column" />
        </button>
        <button class="btn" :class="view === 'table' ? 'btn-primary' : 'btn-outline-primary'" @click="view = 'table'">
          <i class="fa-solid fa-table" />
        </button>
      </div>
    </div>

    <kpi-cards :cards="kpiCards" />

    <!-- TABLE -->
    <div v-if="view === 'table'" class="table-responsive">
      <!-- District x status (single type) -->
      <table v-if="districtMode" class="table table-dark table-sm table-bordered align-middle mb-0">
        <thead>
          <tr>
            <th>District</th>
            <th v-for="c in districtView.columns" :key="c[0]" class="text-end">{{ c[1] }}</th>
            <th class="text-end">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in districtView.rows" :key="r.key">
            <td>{{ r.label }}</td>
            <td v-for="c in districtView.columns" :key="c[0]" class="text-end">{{ r.cells[c[0]] }}</td>
            <td class="text-end fw-semibold">{{ r.total }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Status x type -->
      <table v-else class="table table-dark table-sm table-bordered align-middle mb-0">
        <thead>
          <tr>
            <th>Status</th>
            <th class="text-end">NOD</th>
            <th class="text-end">Emergency</th>
            <th class="text-end">Electrical</th>
            <th class="text-end">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in typeView.rows" :key="r.key">
            <td>{{ r.label }}</td>
            <td class="text-end" :class="{ 'text-secondary': r.cells.kjp === null }">{{ cell(r.cells.kjp) }}</td>
            <td class="text-end" :class="{ 'text-secondary': r.cells.emergency === null }">{{ cell(r.cells.emergency) }}</td>
            <td class="text-end" :class="{ 'text-secondary': r.cells.electrical === null }">{{ cell(r.cells.electrical) }}</td>
            <td class="text-end fw-semibold">{{ rowTotal(r) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="fw-bold table-active">
            <td>Total cases</td>
            <td class="text-end">{{ typeView.totals.kjp }}</td>
            <td class="text-end">{{ typeView.totals.emergency }}</td>
            <td class="text-end">{{ typeView.totals.electrical }}</td>
            <td class="text-end">{{ typeView.grandTotal }}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- CHART -->
    <div v-else>
      <viz-chart type="bar" :data="chartData" :options="stackedOptions" />
    </div>
  </div>
</template>

<script>
import KpiCards from "@/components/viz/KpiCards";
import VizChart from "@/components/viz/VizChart";
import { statusTypeView, statusDistrictView, statusKpis, STATUS_DEFS } from "@/utils/dashboard";
import { downloadXlsx } from "@/utils/exportTable";

export default {
  name: "status-viz-tab",
  props: ["selectedYear", "cases", "districts"],
  components: { KpiCards, VizChart },
  data() {
    return { districtSel: "all", typeSel: "all", view: "chart" };
  },
  computed: {
    districtId() {
      return this.districtSel === "all" ? null : this.districtSel;
    },
    // District breakdown when a single type is chosen at All districts.
    districtMode() {
      return this.typeSel !== "all" && this.districtSel === "all";
    },
    typeView() {
      return statusTypeView(this.cases, { districtId: this.districtId });
    },
    districtView() {
      if (!this.districtMode) return { columns: [], rows: [] };
      return statusDistrictView(this.cases, this.typeSel, this.districts);
    },
    kpiCards() {
      const k = this.statusKpisLocal;
      return [
        { label: "Total cases", value: k.total, variant: "primary" },
        { label: "Completed / Approved", value: k.done, variant: "success" },
        { label: "In progress", value: k.inProgress, variant: "warning" },
        { label: "Rejected", value: k.rejected, variant: "danger" },
      ];
    },
    statusKpisLocal() {
      return statusKpis(this.cases, { districtId: this.districtId });
    },
    chartData() {
      if (this.districtMode) {
        const cols = STATUS_DEFS[this.typeSel];
        return {
          labels: this.districtView.rows.map((r) => r.label),
          datasets: cols.map(([key, label], i) => ({
            label,
            data: this.districtView.rows.map((r) => r.cells[key] || 0),
            backgroundColor: this.statusColor(key, i),
          })),
        };
      }
      // Stacked bars: one stack per type, segments = statuses.
      const types = [["kjp", "NOD"], ["emergency", "Emergency"], ["electrical", "Electrical"]];
      const statusKeys = this.typeView.rows.map((r) => r.key);
      return {
        labels: types.map((t) => t[1]),
        datasets: statusKeys.map((key, i) => {
          const row = this.typeView.rows.find((r) => r.key === key);
          return {
            label: row.label,
            data: types.map((t) => row.cells[t[0]] || 0),
            backgroundColor: this.statusColor(key, i),
          };
        }),
      };
    },
    stackedOptions() {
      return {
        scales: {
          x: { stacked: true },
          y: { stacked: true, beginAtZero: true, title: { display: true, text: "Cases" } },
        },
      };
    },
    // Headers + rows for the Excel export, mirroring whichever table is shown.
    excelExport() {
      if (this.districtMode) {
        const cols = this.districtView.columns;
        const headers = ["District", ...cols.map((c) => c[1]), "Total"];
        const rows = this.districtView.rows.map((r) => [r.label, ...cols.map((c) => r.cells[c[0]] || 0), r.total]);
        return { sheetName: "Status", filename: `status-${this.typeSel}.xlsx`, headers, rows };
      }
      const headers = ["Status", "NOD", "Emergency", "Electrical", "Total"];
      const rows = this.typeView.rows.map((r) => [
        r.label,
        r.cells.kjp == null ? "" : r.cells.kjp,
        r.cells.emergency == null ? "" : r.cells.emergency,
        r.cells.electrical == null ? "" : r.cells.electrical,
        this.rowTotal(r),
      ]);
      rows.push([
        "Total cases",
        this.typeView.totals.kjp,
        this.typeView.totals.emergency,
        this.typeView.totals.electrical,
        this.typeView.grandTotal,
      ]);
      return { sheetName: "Status", filename: "status-by-type.xlsx", headers, rows };
    },
  },
  methods: {
    downloadExcel() {
      const x = this.excelExport;
      downloadXlsx(x.filename, x.sheetName, x.headers, x.rows);
    },
    cell(v) {
      return v === null ? "—" : v;
    },
    rowTotal(r) {
      return ["kjp", "emergency", "electrical"].reduce((s, t) => s + (r.cells[t] || 0), 0);
    },
    palette(i) {
      const c = ["#6c757d", "#0d6efd", "#fd7e14", "#198754", "#dc3545", "#6f42c1"];
      return c[i % c.length];
    },
    // Status-keyed colors so a status is always the same color in every view
    // and matches the KPI card semantics (approved/completed green-family,
    // rejected red). Index palette alone colored Rejected green in the
    // Emergency/Electrical district view and collided approved with
    // site_visit (both grey) in the 7-status stacked view.
    statusColor(key, i) {
      const colors = {
        site_visit: "#6c757d",
        nod: "#0d6efd",
        work_order: "#fd7e14",
        claims: "#6f42c1",
        approved: "#20c997",
        completed: "#198754",
        rejected: "#dc3545",
      };
      return colors[key] || this.palette(i);
    },
  },
};
</script>
